import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { trackEvent } from '@/lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const VerifyEmail = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage(t('Invalid verification link'));
        return;
      }

      try {
        const response = await axios.post(`${API}/api/auth/verify-email/${token}`);

        if (response.status === 200) {
          setStatus('success');
          setMessage(response.data.message || t('Email verified successfully!'));
          setEmail(response.data.email);
          // Funnel-Tracking (Paket A, Juni 2026)
          trackEvent('email_verified');

          // Soft-Gate: bereits eingeloggte Nutzer landen direkt in der App
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.detail || t('Verification failed'));
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage(error.response?.data?.detail || t('An error occurred during verification'));
      }
    };

    verifyEmail();
  }, [searchParams, navigate, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('Verifying Email...')}
              </h1>
              <p className="text-gray-600">
                {t('Please wait while we verify your email address')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('Email Verified!')}
              </h1>
              <p className="text-gray-600 mb-4">
                {message}
              </p>
              {email && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <Mail className="w-5 h-5 text-green-600 inline mr-2" />
                  <span className="text-green-800">{email}</span>
                </div>
              )}
              <p className="text-sm text-gray-500">
                Du wirst gleich weitergeleitet …
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 w-full bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 transition-colors"
                data-testid="verify-goto-app"
              >
                Zur App
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('Verification Failed')}
              </h1>
              <p className="text-gray-600 mb-4">
                {message}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('Go to Login')}
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('Back to Registration')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
